import { Fragment, ReactNode, useEffect, useState } from "react";

import useBidStore from "@/stores/Bid";

import {
  BidCategory,
  EnterpriseRestriction,
  GetBidListRequest,
  InternationFlag,
  OrderBy,
  SortBy,
  ValueType,
} from "@/types/bid";
import { SearchConditionItem } from "@/types/setting";

import {
  BidTypeCd,
  categoryList,
  enterpriseList,
  sortList,
  internationList,
  DateRangeList,
  ProjectType,
  DefaultBidSearch,
  BidNoticeSuggestSearchClientList,
  BidNoticeSuggestSearchLicenseList,
  BidNoticeSuggestSearchProductList,
} from "@/consts/Bid";

import { KeywordSearch } from "@/types/search";

import { formattedOnlyDate, isNotEmpty, subtractDays } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import { GetBidRegionList } from "@/services/Bid";

import CommonSelect from "@/components/Common/Select";
import CommonButton from "@/components/Common/Button";
import CommonImg from "@/components/Common/Img";
import KeywordSearchItem from "@/components/KeywordSearchItem";
import KeywordExcludeItem from "@/components/KeywordExcludeItem";
import CommonCheckbox from "@/components/Common/Checkbox";
import FilterItemLabel from "@/components/FilterItemLabel";
import CommonInput from "@/components/Common/Input";
import { DateRangePicker } from "@/components/DateRangePicker";
import FilterRadioItem from "@/components/FilterRadioItem";
import { Slider } from "@/components/ui/slider";
import CommonButtonToggle from "@/components/Common/ButtonToggle";
import CommonPagination from "@/components/Common/Pagination";
import BidLiveTextSearch from "@/components/BidLiveTextSearch";
import DialogKeywordGroup from "@/components/DialogKeywordGroup";
import BidNumberSearchItem from "@/components/BidNumberSearchItem";
import LiveTextSearch from "@/components/LiveTextSearch";

import ChevronUpIcon from "@/assets/images/chevron-icon-up.png";
import ChevronDownIcon from "@/assets/images/chevron-icon-down.png";

type Props = {
  projectType: string;
  onSearch: (toastFinish: boolean, newFilter?: GetBidListRequest) => void;
  children: ReactNode;
  totalItems?: number;
  searchConditionList: SearchConditionItem[] | [];
  onChangeCondition: (searchId: string) => void;
  onSaveSearchCondition: () => void;
  onRefreshSearchCondition: () => void;
};

export default function BidSearch({
  projectType,
  onSearch,
  children,
  totalItems,
  searchConditionList,
  onChangeCondition,
  onSaveSearchCondition,
  onRefreshSearchCondition,
}: Props) {
  const storedFilter = useBidStore((state) => state.filter);
  const setStoredFilter = useBidStore((state) => state.setFilter);

  const [open, setOpen] = useState<boolean>(false);
  const [openExplanation, setOpenExplanation] = useState<boolean>(false);
  const [regionList, setRegionList] = useState<ValueType[]>([]);

  // 기본 키워드셋을 2개로 제한
  useEffect(() => {
    if (storedFilter?.settings?.keyword_sets.length > 2) {
      handleChangeFilter({
        keyword_sets: storedFilter.settings.keyword_sets.slice(0, 2),
      });
    }
  }, []);

  const isOriginal = projectType === ProjectType.Original;

  const getNoticeNoExplanation = (keywordList: string[]) => {
    if (keywordList.length > 0) {
      const isOver3 = keywordList.length > 3;
      const input_strings = keywordList
        .slice(0, 3)
        .map((string, stringIndex) => (
          <span key={stringIndex} className="text-blue-ThermoFisher">
            {string}
          </span>
        ));
      return (
        <Fragment>
          공고 번호에서 &nbsp;
          {input_strings.map((el, elIndex) => (
            <span key={elIndex}>
              {el}
              {elIndex < input_strings.length - 1 && <>,&nbsp;</>}
            </span>
          ))}
          &nbsp;
          {isOver3 && <>등</>}을 포함하고,&nbsp;
        </Fragment>
      );
    }
  };
  const getExcludeExplanation = (type: string, keywordList: string[]) => {
    if (keywordList.length > 0) {
      const isOver3 = keywordList.length > 3;
      const input_strings = keywordList
        .slice(0, 3)
        .map((string, stringIndex) => (
          <span key={stringIndex} className="text-pink">
            {string}
          </span>
        ));
      return (
        <Fragment>
          {`${type}에서`}
          &nbsp;
          {input_strings.map((el, elIndex) => (
            <span key={elIndex}>
              {el}
              {elIndex < input_strings.length - 1 && <>,&nbsp;</>}
            </span>
          ))}
          &nbsp;
          {isOver3 && <>등</>}을 제외하고,&nbsp;
        </Fragment>
      );
    }
  };

  const handleChangeFilter = (props: any) => {
    const newSettings = { ...storedFilter.settings, ...props };
    const newFilter = {
      ...storedFilter,
      settings: newSettings,
    };
    setStoredFilter(newFilter);
    if (Object.keys(props).includes("page")) {
      onSearch(false, newFilter);
    }
  };

  const handleClickSearch = () => {
    showToast({
      message: "공고를 검색합니다.",
    });
    const newFilter = {
      ...storedFilter,
      settings: { ...storedFilter.settings, page: 1 },
    };
    setStoredFilter(newFilter);
    onSearch(true, newFilter);
  };

  const handleSelectDateRangePicker = (e: any) => {
    const newFrom = e.from;
    let newTo = e.to;

    handleChangeFilter({
      min_date: formattedOnlyDate(newFrom),
      max_date: formattedOnlyDate(newTo),
    });
  };

  const handleClickSearchFilterReset = () => {
    setStoredFilter({
      ...storedFilter,
      settings: { ...DefaultBidSearch, project_type: projectType },
    });
  };

  const onLoad = async () => {
    if (!isOriginal) return;
    try {
      const result = await GetBidRegionList();
      setRegionList(
        result.data.pods[0].content.items.map((item) => ({
          label: item.label,
          value: item.code,
        }))
      );
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    storedFilter && (
      <div className="flex flex-col">
        <div className="w-full bg-white rounded-[12px] p-[20px] my-[20px] flex flex-col gap-[12px] shadow-[0_3.5px_5.5px_0_rgba(0,0,0,0.05)]">
          <div className="flex justify-between">
            <div className="flex gap-[8px] items-center">
              <span className="w-[64px] text-gray-B text-xs">검색 조건</span>
              <span />
              <CommonSelect
                defaultValue={
                  searchConditionList.length > 0
                    ? (searchConditionList.find(
                        (condition) =>
                          condition.search_id === storedFilter.search_id
                      )?.search_id ?? "")
                    : ""
                }
                className="min-w-[156px]"
                optionList={searchConditionList.map((condition) => ({
                  label: condition.search_name,
                  value: condition.search_id,
                }))}
                onChange={(value) => {
                  onChangeCondition(value);
                }}
                useAll={false}
              />
              <CommonButton
                label="검색 조건 저장"
                onClick={onSaveSearchCondition}
              />
              <DialogKeywordGroup onRefresh={onRefreshSearchCondition} />
              <CommonButton
                className="px-2 py-1.5 text-xs font-semibold"
                label="검색 필터 초기화"
                onClick={handleClickSearchFilterReset}
              />
            </div>
          </div>
          <BidNumberSearchItem
            data={storedFilter.settings.notice_no}
            setData={(newData) => {
              handleChangeFilter({
                notice_no: newData,
              });
            }}
          />

          {/* 기관 */}
          <div className="flex items-center gap-[8px]">
            <div className="w-[80px]">
              <FilterItemLabel label="기관" />
            </div>
            <BidLiveTextSearch
              placeholder="기관 이름을 입력해주세요"
              method={BidNoticeSuggestSearchClientList}
              defaultValue={storedFilter.settings.dminst_filter}
              onChange={(valueList: ValueType[]) => {
                handleChangeFilter({
                  dminst_filter: valueList,
                });
              }}
              itemKey="client"
              badgeColor="bg-mediumGreen"
              showPlusButton={false}
              enableEnterKey={false}
            />
          </div>

          {/* 물품번호 */}
          <div className="flex items-center gap-[8px]">
            <div className="w-[80px]">
              <FilterItemLabel label="물품번호" />
            </div>
            <BidLiveTextSearch
              placeholder="물품 번호를 입력해주세요"
              method={BidNoticeSuggestSearchProductList}
              defaultValue={storedFilter.settings.products_filter}
              onChange={(valueList: ValueType[]) => {
                handleChangeFilter({
                  products_filter: valueList,
                });
              }}
              itemKey="product"
              badgeColor="bg-blue-ThermoFisher"
              showPlusButton={false}
              enableEnterKey={false}
            />
          </div>

          {/* 업종번호 */}
          <div className="flex items-center gap-[8px]">
            <div className="w-[80px]">
              <FilterItemLabel label="업종번호" />
            </div>
            <BidLiveTextSearch
              placeholder="업종 번호를 입력해주세요"
              method={BidNoticeSuggestSearchLicenseList}
              defaultValue={storedFilter.settings.license_filter}
              onChange={(valueList: ValueType[]) => {
                handleChangeFilter({
                  license_filter: valueList,
                });
              }}
              itemKey="license"
              badgeColor="bg-blue-B"
              showPlusButton={false}
              enableEnterKey={false}
            />
          </div>

          {/* 공고제목, 첨부파일본문 키워드 검색 */}
          {storedFilter?.settings?.keyword_sets
            .slice(0, 2)
            .map((keyword: KeywordSearch, index: number) => (
              <KeywordSearchItem
                key={index}
                data={keyword}
                setData={(newData) => {
                  handleChangeFilter({
                    keyword_sets: [
                      ...storedFilter.settings.keyword_sets.toSpliced(
                        index,
                        1,
                        newData
                      ),
                    ],
                  });
                }}
              />
            ))}

          {open && (
            <KeywordExcludeItem
              label={"제목 제외 키워드"}
              data={storedFilter.settings.title_exclude_strings}
              setData={(newData) => {
                handleChangeFilter({
                  title_exclude_strings: newData,
                });
              }}
            />
          )}
          {open && (
            <KeywordExcludeItem
              label={"본문 제외 키워드"}
              data={storedFilter.settings.exclude_strings}
              setData={(newData) => {
                handleChangeFilter({ exclude_strings: newData });
              }}
            />
          )}

          {/* Search Filters */}
          {open && (
            <div className="mt-2 flex flex-col gap-[12px]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex items-center gap-[8px] flex-wrap">
                  <div className="w-[80px]">
                    <FilterItemLabel label="공고일" />
                  </div>
                  <DateRangePicker
                    defaultValue={{
                      from: new Date(storedFilter.settings.min_date),
                      to: new Date(storedFilter.settings.max_date),
                    }}
                    disabled={
                      storedFilter.settings.date_range_option_index !== "0"
                    }
                    onSelect={(e: any) => {
                      handleSelectDateRangePicker(e);
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="마감일 지난 공고 포함"
                    defaultValue={storedFilter.settings.include_finished}
                    onChange={(value) => {
                      handleChangeFilter({ include_finished: value });
                    }}
                  />
                </div>
                <div className="ml-[88px]">
                  <FilterRadioItem
                    name="notice_date"
                    wrapperClassName="grid grid-cols-6 gap-y-3 justify-start w-[400px]"
                    defaultValue={storedFilter.settings.date_range_option_index}
                    radioList={DateRangeList}
                    onChange={(value) => {
                      const maxDate = new Date();
                      if (value !== "0" && value !== "999") {
                        handleChangeFilter({
                          min_date: formattedOnlyDate(
                            subtractDays(maxDate, Number(value)).toString()
                          ),
                          max_date: formattedOnlyDate(maxDate.toString()),
                          date_max_range: false,
                          date_range_option_index: value,
                        });
                      } else if (value === "999") {
                        handleChangeFilter({
                          min_date: "2023-01-01",
                          date_max_range: true,
                          date_range_option_index: value,
                        });
                      } else {
                        handleChangeFilter({
                          date_range_option_index: value,
                        });
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-[8px] flex-wrap">
                <div className="w-[80px]">
                  <FilterItemLabel label="사업금액" />
                </div>
                <div className="flex items-center gap-[4px]">
                  <CommonInput
                    type="number"
                    className="min-w-auto w-[125px]"
                    disabled={storedFilter.settings.budget_no_limit}
                    defaultValue={storedFilter.settings.min_budget}
                    onChange={(value: number) => {
                      handleChangeFilter({ min_budget: value });
                    }}
                    onBlur={(value: number) => {
                      if (value > storedFilter.settings.max_budget) {
                        showToast({
                          type: "warning",
                          message: "최소 금액은 최대 금액보다 클 수 없습니다.",
                        });
                        const newMinBudget =
                          storedFilter.settings.max_budget - 1 < 0
                            ? 0
                            : storedFilter.settings.max_budget - 1;
                        handleChangeFilter({
                          min_budget: newMinBudget,
                        });
                      }
                    }}
                  />
                  <span className="text-[10px] font-bold text-gray-B">~</span>
                  <CommonInput
                    type="number"
                    className="min-w-auto w-[125px]"
                    disabled={storedFilter.settings.budget_no_limit}
                    defaultValue={storedFilter.settings.max_budget}
                    onChange={(value: number) => {
                      handleChangeFilter({ max_budget: value });
                    }}
                    onBlur={(value: number) => {
                      if (value < storedFilter.settings.min_budget) {
                        showToast({
                          type: "warning",
                          message:
                            "최대 금액은 최소 금액보다 작을 수 없습니다.",
                        });
                        handleChangeFilter({
                          max_budget: storedFilter.settings.min_budget + 1,
                        });
                      }
                    }}
                  />
                </div>
                <CommonCheckbox
                  size="sm"
                  label="금액 제한 없음"
                  defaultValue={storedFilter.settings.budget_no_limit}
                  onChange={(value: boolean) => {
                    handleChangeFilter({ budget_no_limit: value });
                  }}
                />
              </div>

              <div className="mt-3 flex items-center gap-[8px] flex-wrap">
                <div className="w-[80px]">
                  <FilterItemLabel label="사업 구분" />
                </div>
                <CommonSelect
                  className="min-w-[130px]"
                  defaultValue={storedFilter.settings.category}
                  optionList={categoryList}
                  useAll={true}
                  onChange={(value) => {
                    handleChangeFilter({
                      category: value as BidCategory,
                    });
                  }}
                />
              </div>

              <div className="flex items-center gap-[8px] flex-wrap mt-[16px] mb-[16px]">
                <div className="w-[80px]">
                  <FilterItemLabel label="조건" />
                </div>
                <div className="flex flex-wrap gap-x-[16px] gap-y-[8px]">
                  <CommonCheckbox
                    size="sm"
                    label="업종조건 충족"
                    defaultValue={storedFilter.settings.license_check}
                    onChange={(value) => {
                      handleChangeFilter({ license_check: value });
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="물품조건 충족"
                    defaultValue={storedFilter.settings.product_check}
                    onChange={(value) => {
                      handleChangeFilter({ product_check: value });
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="공동수급 허용"
                    defaultValue={storedFilter.settings.consortium_check}
                    onChange={(value) => {
                      handleChangeFilter({ consortium_check: value });
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="실적제한 없음"
                    defaultValue={
                      storedFilter.settings.performance_flag === "RESTRICTION"
                    }
                    onChange={(value) => {
                      handleChangeFilter({
                        performance_flag: value
                          ? "RESTRICTION"
                          : "NO_RESTRICTION",
                      });
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="인적제한 없음"
                    defaultValue={
                      storedFilter.settings.expert_flag === "RESTRICTION"
                    }
                    onChange={(value) => {
                      handleChangeFilter({
                        expert_flag: value ? "RESTRICTION" : "NO_RESTRICTION",
                      });
                    }}
                  />
                </div>
              </div>

              {isOriginal && (
                <div className="flex flex-col gap-[12px]">
                  <div className="flex items-center justify-between w-[600px]">
                    <div className="w-[80px]">
                      <FilterItemLabel label="지역제한" />
                    </div>
                    <CommonCheckbox
                      size="sm"
                      label="지역 전체 선택"
                      defaultValue={
                        storedFilter.settings.region_filter.length ===
                        regionList.length
                      }
                      onChange={(value) => {
                        handleChangeFilter({
                          region_filter: value ? regionList : [],
                        });
                      }}
                    />
                  </div>
                  {regionList && (
                    <div className="grid grid-cols-6 auto-rows-[30px] gap-x-[8px] gap-y-[8px] w-[600px]">
                      {regionList.map((button) => (
                        <CommonButtonToggle
                          key={`${button.value}`}
                          label={button.label}
                          value={button.value}
                          toggleValue={storedFilter.settings.region_filter.some(
                            (filterItem: ValueType) =>
                              filterItem.value === button.value
                          )}
                          onClick={(item: ValueType) => {
                            handleChangeFilter({
                              region_filter:
                                storedFilter.settings.region_filter.some(
                                  (filterItem: ValueType) =>
                                    filterItem.value === item.value
                                )
                                  ? storedFilter.settings.region_filter.filter(
                                      (region: ValueType) =>
                                        region.value !== item.value
                                    )
                                  : [
                                      ...storedFilter.settings.region_filter,
                                      item,
                                    ],
                            });
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between">
            <div
              className="text-gray-B text-xs min-w-[300px] flex justify-center items-center cursor-pointer"
              onClick={() => setOpen((prev) => !prev)}
            >
              추가 검색 조건 목록 {open ? "접기" : "열기"}
              <CommonImg
                src={open ? ChevronUpIcon : ChevronDownIcon}
                alt="Chevron"
                className="size-[16px]"
              />
            </div>
            <div className="flex-shrink-0">
              <CommonButton label={"검색하기"} onClick={handleClickSearch} />
            </div>
          </div>
        </div>
        <div className="w-full flex justify-center items-top pb-[20px]">
          {openExplanation ? (
            <div className="w-full flex justify-center flex-wrap font-semibold">
              {storedFilter?.settings?.keyword_sets.map(
                (keyword: KeywordSearch, keywordIndex: number) => {
                  if (keyword.input_strings.length > 0) {
                    const isOver3 = keyword.input_strings.length > 3;
                    const input_strings = keyword.input_strings
                      .slice(0, 3)
                      .map((string, stringIndex) => (
                        <span key={stringIndex} className="text-dark-red">
                          {string}
                        </span>
                      ));
                    return (
                      <Fragment key={keywordIndex}>
                        {`${
                          keyword.title_or_doc === "TITLE"
                            ? "공고 제목"
                            : "첨부파일 본문"
                        }에서`}
                        &nbsp;
                        {input_strings.map((el, elIndex) => (
                          <Fragment key={elIndex}>
                            {el}
                            {elIndex < input_strings.length - 1 && (
                              <>&nbsp;{keyword.or_and}&nbsp;</>
                            )}
                          </Fragment>
                        ))}
                        &nbsp;
                        {isOver3 && <>등</>}을 포함하고,&nbsp;
                      </Fragment>
                    );
                  }
                }
              )}
              {getNoticeNoExplanation(storedFilter.settings.notice_no)}
              {getExcludeExplanation(
                "제목",
                storedFilter.settings.title_exclude_strings
              )}
              {getExcludeExplanation(
                "첨부 파일 본문",
                storedFilter.settings.exclude_strings
              )}
              사업구분은&nbsp;
              <span className="text-dark-red">
                {BidTypeCd[storedFilter.settings.category]}
              </span>
              &nbsp;에 해당하는 공고를 찾습니다.
            </div>
          ) : (
            <div className="w-full"></div>
          )}
          <div className="min-w-max items-end">
            <CommonCheckbox
              label="설명 보기"
              defaultValue={openExplanation}
              onChange={(value) => setOpenExplanation(value)}
            />
          </div>
        </div>
        <div className="flex flex-col w-full">
          {/* Search result count */}
          {totalItems !== undefined && totalItems > 0 && (
            <div className="text-xs font-semibold mb-[16px]">
              검색 결과{" "}
              <span className="text-dark-red">
                {totalItems.toLocaleString()}
              </span>
              개
            </div>
          )}

          {/* Real-time filters above table */}
          {totalItems !== undefined && totalItems > 0 && (
            <div className="flex items-center gap-[16px] flex-wrap mb-[16px]">
              <div className="flex items-center gap-[8px]">
                <FilterItemLabel label="기업 제한" />
                {enterpriseList && (
                  <CommonSelect
                    className="min-w-[130px]"
                    defaultValue={storedFilter.settings.enterprise_restriction}
                    optionList={enterpriseList}
                    onChange={(value) => {
                      handleChangeFilter({
                        enterprise_restriction: value as EnterpriseRestriction,
                      });
                      onSearch(false);
                    }}
                  />
                )}
              </div>

              <div className="flex items-center gap-[8px]">
                <FilterItemLabel label="정렬 기준" />
                {sortList && (
                  <CommonSelect
                    className="min-w-[130px]"
                    defaultValue={`${storedFilter.settings.sort_by}|${storedFilter.settings.order}`}
                    optionList={sortList}
                    useAll={false}
                    onChange={(value) => {
                      const [sortValue, orderValue] = value.split("|");
                      handleChangeFilter({
                        sort_by: sortValue as SortBy,
                        order: orderValue as OrderBy,
                      });
                      onSearch(false);
                    }}
                  />
                )}
              </div>

              {isOriginal && (
                <div className="flex items-center gap-[8px]">
                  <FilterItemLabel label="국제입찰 기준" />
                  {internationList && (
                    <CommonSelect
                      className="min-w-[130px]"
                      defaultValue={storedFilter.settings.international_flag}
                      optionList={internationList}
                      onChange={(value) => {
                        handleChangeFilter({
                          international_flag: value as InternationFlag,
                        });
                        onSearch(false);
                      }}
                    />
                  )}
                </div>
              )}

              <div className="flex items-center gap-[8px]">
                <FilterItemLabel label="제품비중" />
                <div className="flex gap-[4px] items-center">
                  <CommonInput
                    className="w-[30px] h-[20px]"
                    defaultValue={storedFilter.settings.own_product_ratio}
                    maxLength={2}
                    onChange={(value) => {
                      const regExp = new RegExp(/^\s*$|^\s*\d{1,2}\s*$/);
                      if (!regExp.test(value)) {
                        return;
                      }
                      handleChangeFilter({
                        own_product_ratio: Number(value.trim()) || 0,
                      });
                    }}
                    onBlur={(value) => {
                      if (!isNotEmpty(value)) {
                        handleChangeFilter({ own_product_ratio: 0 });
                      }
                      onSearch(false);
                    }}
                  />
                  <span className="text-[10px] text-gray-B font-semibold">
                    ~ 100%
                  </span>
                  <div className="ml-[8px]">
                    <div
                      className="w-[140px] pointer-events-none custom-slider"
                      style={
                        {
                          "--slider-track-color": "#e2cecf",
                          "--slider-range-color": "rgba(167, 109, 110, 0.20)",
                          "--slider-thumb-color": "#e2cecf",
                          "--slider-border-color": "#d1d5db",
                        } as React.CSSProperties
                      }
                    >
                      <Slider
                        className="w-full"
                        value={[storedFilter.settings.own_product_ratio]}
                        max={99}
                        step={1}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-[16px]">{children}</div>

          {isNotEmpty(totalItems) && (
            <CommonPagination
              itemsPerPage={storedFilter.settings.page_size}
              totalItems={totalItems ?? 0}
              currentPage={storedFilter.settings.page}
              onPageChange={(page) => {
                handleChangeFilter({ page: page });
              }}
            />
          )}
        </div>
      </div>
    )
  );
}
